import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { formatCompactCurrency } from "../lib/format";
import { macroFeatureLabel } from "../lib/macro-feature-labels";
import { withVolumes } from "../lib/macro-volumes";
import { TICKET_SIZE_OPTIONS, useTicketSize } from "../lib/ticket-size";
import { cohortColor } from "../lib/api/lifetime-loss-projections";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { SensitivityBubbleChart } from "../../../components/PortfolioPerformance/charts/sensitivity-bubble-chart";
import { VariableBubbleChart } from "../../../components/PortfolioPerformance/charts/variable-bubble-chart";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { MacroTierConfigSection } from "../../../components/PortfolioPerformance/macro-tier-config";
import { isProductAdmin } from "../../../components/PortfolioPerformance/access-control";
import { TIER_COLOR, TIER_ORDER, tierBands, tierForScore } from "../../../components/PortfolioPerformance/macro-tier-colors";
import {
  useMacroExposure,
  useMacroPeriodAverage,
  useMacroTiers,
  useMacroVariables,
  useMacroVariableTrend,
  useMacroVolumes,
  type IndustryPoint,
  type MacroTier,
  type MacroVariablePoint,
  type TrendPeriod,
  type SensitivityTier,
} from "../lib/api/macro-exposure";

// The names the Macro Variable Trend chart gives its five indicators (the rest of the page keeps the shared labels).
const TREND_INDICATOR_LABELS: Record<string, string> = {
  rate_shock: "Rate Shock",
  rate_shock_3m_change: "Rate Shock",
  cpi_roll3m: "CPI Roll 3M",
  gdp_roll3q: "GDP Roll 3Q",
  optimism_index: "Optimism Index",
  industrialprod_change6m: "Industrial Production Change 6M",
};

/** A band's name in its colour (red High, amber Medium, green Low). */
function TierText({ tier }: { tier: SensitivityTier }) {
  return (
    <span className="font-medium" style={{ color: TIER_COLOR[tier] }}>
      {tier}
    </span>
  );
}

/** Each named factor's label for one industry: its weight as a share of the strongest factor's weight, on the same
 *  0-100 scale and the same bands as the sensitivity score, so a factor reads High, Medium or Low. */
function factorTiers(factors: Array<{ factor: string; weight: number }>, tiers: MacroTier[] | undefined) {
  const strongest = Math.max(0, ...factors.map((f) => Math.abs(f.weight)));
  return new Map(
    factors.map((f) => [f.factor, tierForScore(strongest > 0 ? (Math.abs(f.weight) / strongest) * 100 : 0, tiers)]),
  );
}

/** In place of a chart when the chosen ticket band has no scores in the quarter (Mid-market / Large today: the scored
 *  book is Under 250K). The band stays selectable so the page fills in on its own if that data ever arrives. */
function NoBandData({ label }: { label: string | undefined }) {
  return (
    <p className="py-10 text-center text-sm text-muted-foreground">
      No macro sensitivity data for {label ? `${label} tickets` : "this ticket size"} yet.
    </p>
  );
}

/** The Macro Sensitivity Score graph (also called MAF): the average sensitivity score over time, one point per year or quarter (athena_decisions.macro_sensitivity_period_avg): a line over
 *  the same fixed 0-100 axis and Sensitivity Tier Configuration zones as the other sensitivity charts. The tooltip names the band only
 *  (Low, Medium or High), never the score. */
function AverageSensitivityPanel({
  tiers,
  bandNote,
  bandLabel,
}: {
  tiers: MacroTier[] | undefined;
  bandNote: string;
  bandLabel: string | undefined;
}) {
  const [period, setPeriod] = useState<TrendPeriod>("year");
  const averageQ = useMacroPeriodAverage(period);
  const data = averageQ.data;
  const rows = useMemo(() => (data?.points ?? []).map((p) => ({ month: p.label, score: p.score })), [data]);
  const bands = tierBands(tiers).map((b) => ({ label: b.tier, from: b.from, to: b.to, color: TIER_COLOR[b.tier] }));

  return (
    <Panel
      title="Macro Sensitivity Score"
      description={
        data?.hasData
          ? `The 0 to 100 score averaged over all industries, ${period === "year" ? "per year" : "per quarter"}, as of ${data.asOf}${bandNote}`
          : "The macro sensitivity score over time"
      }
      right={
        <ToggleGroup
          type="single"
          value={period}
          onValueChange={(next) => next && setPeriod(next as TrendPeriod)}
          className="border border-border bg-secondary"
          aria-label="Macro sensitivity score period"
        >
          <ToggleGroupItem value="year" className="h-9 px-3 text-xs" title="One point per year">
            Annual
          </ToggleGroupItem>
          <ToggleGroupItem value="quarter" className="h-9 px-3 text-xs" title="One point per quarter">
            Quarterly
          </ToggleGroupItem>
        </ToggleGroup>
      }
    >
      {averageQ.isError ? (
        <p className="text-sm text-destructive">Could not load the macro sensitivity score.</p>
      ) : averageQ.isPending ? (
        <Skeleton className="h-60 w-full" />
      ) : data === null || data === undefined ? (
        <p className="text-sm text-muted-foreground">Not Available Yet.</p>
      ) : !data.hasData ? (
        <NoBandData label={bandLabel} />
      ) : (
        <TrendLineChart
          data={rows}
          series={[{ dataKey: "score", name: "Macro sensitivity score", color: "var(--amber)" }]}
          suffix=""
          decimals={1}
          height={300}
          bands={bands}
          yDomain={[0, 100]}
          allXLabels={period === "quarter"}
          valueLabel={({ value }) => <TierText tier={tierForScore(value, tiers)} />}
        />
      )}
    </Panel>
  );
}

/** How many industries sit in each band: the band's name and its count, never the score range behind it. */
function TierStrip({ counts }: { counts: Array<{ tier: SensitivityTier; count: number }> }) {
  return (
    <div className="flex flex-wrap gap-4">
      {counts.map((c) => (
        <div key={c.tier} className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="size-2.5" style={{ background: TIER_COLOR[c.tier] }} aria-hidden />
          {c.tier}:{" "}
          <span className="tabular-nums text-foreground">{c.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function MacroExposurePage() {
  const { data, isPending, isError, error } = useMacroExposure();
  const volumesQ = useMacroVolumes();
  const tiersQ = useMacroTiers();
  const variablesQ = useMacroVariables();
  // Annual (change vs the same quarter a year earlier) is the default; Quarterly is the quarter-on-quarter view.
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>("year");
  // Variables switched off with their chip above the trend chart (all on to start); keyed by series id.
  const [hiddenTrend, setHiddenTrend] = useState<ReadonlySet<string>>(new Set());
  const trendQ = useMacroVariableTrend(trendPeriod);
  const [selected, setSelected] = useState<IndustryPoint | null>(null);
  const [selectedVariable, setSelectedVariable] = useState<MacroVariablePoint | null>(null);
  // The page-level Ticket Size toggle. Each band is scored within itself, so the descriptions say so.
  const { ticket } = useTicketSize();
  const bandLabel = TICKET_SIZE_OPTIONS.find((option) => option.value === ticket)?.label;
  const bandNote = bandLabel ? ` · ${bandLabel} tickets, ranked within the band` : "";
  // An open side panel belongs to the band it was opened in.
  useEffect(() => {
    setSelected(null);
    setSelectedVariable(null);
  }, [ticket]);
  // The open industry's factors, each labelled High / Medium / Low (see factorTiers).
  const factorLabels = useMemo(
    () => (selected ? factorTiers(selected.factors, tiersQ.data?.tiers) : new Map<string, SensitivityTier>()),
    [selected, tiersQ.data],
  );

  // The model's feature names, given the names a reader should see (the same labels the factor list uses).
  const variablePoints = useMemo<MacroVariablePoint[]>(
    () => (variablesQ.data?.points ?? []).map((p) => ({ ...p, name: macroFeatureLabel(p.name) })),
    [variablesQ.data],
  );

  // One line per variable, one row per x entry (a year, or a quarter): the change against the comparison quarter (a year
  // earlier, or the quarter before). An entry with nothing to compare against has no value, so its line starts later.
  const trend = useMemo(() => {
    const t = trendQ.data;
    if (!t) return { rows: [] as Array<Record<string, string | number | null>>, series: [] };
    const rows = t.quarters.map((q) => {
      const row: Record<string, string | number | null> = { month: q.label };
      // The 0-100 level (the averaged scaled_value), not the change: a change goes negative, a sensitivity never does.
      for (const s of t.series) row[s.id] = s.points.find((p) => p.quarter === q.quarter)?.level ?? null;
      return row;
    });
    const series = t.series.map((s, i) => ({
      dataKey: s.id,
      name: TREND_INDICATOR_LABELS[s.name] ?? macroFeatureLabel(s.name),
      color: cohortColor(i),
    }));
    return { rows: rows as Array<{ month: string }>, series };
  }, [trendQ.data]);

  // The trend plots each variable's 0-100 level (the same figure the Macro Variables bubble chart above tiers into High / Medium / Low),
  // over the same editable tier bands; the tooltip names the band instead of printing the number.
  const trendBands = tierBands(tiersQ.data?.tiers).map((b) => ({ label: b.tier, from: b.from, to: b.to, color: TIER_COLOR[b.tier] }));
  const trendLabel = (entry: { value: number }) => <TierText tier={tierForScore(entry.value, tiersQ.data?.tiers)} />;

  const assetPoints = useMemo(
    () => (data ? withVolumes(data.asset, volumesQ.data?.asset) : []),
    [data, volumesQ.data],
  );
  const borrowerPoints = useMemo(
    () => (data ? withVolumes(data.borrower, volumesQ.data?.borrower) : []),
    [data, volumesQ.data],
  );

  return (
    <div>
      <PageHeader
        title="Macro Exposure"
        subtitle="Asset-industry and borrower-industry credit-risk sensitivity to macro conditions, scored 0 to 100 each quarter and read alongside where the book is concentrated."
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load macro exposure data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending ? (
        <div className="mt-8 grid grid-cols-2 gap-6">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      ) : data === null ? (
        <div className="mt-8 border border-border bg-card p-5 text-sm text-muted-foreground">
          Not Available Yet.
        </div>
      ) : (
        <div className="relative mt-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="Asset Industry Sensitivity"
              description={`Bubble size = owned NI, height = sensitivity (Low, Medium or High), as of ${data.asOf}${bandNote}`}
              right={data.hasData ? <TierStrip counts={data.asset.tierCounts} /> : undefined}
            >
              {data.hasData ? (
                <SensitivityBubbleChart points={assetPoints} onSelect={setSelected} tiers={tiersQ.data?.tiers} />
              ) : (
                <NoBandData label={bandLabel} />
              )}
            </Panel>
            <Panel
              title="Borrower Industry Sensitivity"
              description={`Bubble size = owned NI, height = sensitivity (Low, Medium or High), as of ${data.asOf}${bandNote}`}
              right={data.hasData ? <TierStrip counts={data.borrower.tierCounts} /> : undefined}
            >
              {data.hasData ? (
                <SensitivityBubbleChart points={borrowerPoints} onSelect={setSelected} tiers={tiersQ.data?.tiers} />
              ) : (
                <NoBandData label={bandLabel} />
              )}
            </Panel>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <AverageSensitivityPanel tiers={tiersQ.data?.tiers} bandNote={bandNote} bandLabel={bandLabel} />
          <Panel
            title="Macro Variable Change"
            description={
              variablesQ.data
                ? `Bubble height = the variable's sensitivity (Low, Medium or High), averaged over all industries, as of ${variablesQ.data.asOf}${bandNote}`
                : "How sensitive each macro variable is"
            }
          >
            {variablesQ.isError ? (
              <p className="text-sm text-destructive">Could not load the macro variables.</p>
            ) : variablesQ.isPending ? (
              <Skeleton className="h-60 w-full" />
            ) : variablesQ.data === null ? (
              <p className="text-sm text-muted-foreground">Not Available Yet.</p>
            ) : !variablesQ.data.hasData ? (
              <NoBandData label={bandLabel} />
            ) : (
              <>
                <VariableBubbleChart
                  points={variablePoints}
                  tiers={tiersQ.data?.tiers}
                  onSelect={(p) => setSelectedVariable(p)}
                  height={300}
                />
                {variablePoints.some((p) => p.level === null) ? (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Not shown (no value this quarter):{" "}
                    {variablePoints
                      .filter((p) => p.level === null)
                      .map((p) => p.name)
                      .sort((a, b) => a.localeCompare(b))
                      .join(", ")}
                  </p>
                ) : null}
              </>
            )}
          </Panel>
          </div>

          <Panel
            className="mt-6"
            title="Macro Variable Trend"
            description={
              trendQ.data
                ? `${
                    trendPeriod === "year" ? "Each year's last quarter" : "Each quarter"
                  }: sensitivity of the ${trendQ.data.series.length} macro variables, on a 0 to 100 scale, as of ${trendQ.data.asOf}${bandNote}`
                : "Sensitivity of the macro variables"
            }
            right={
              <ToggleGroup
                type="single"
                value={trendPeriod}
                onValueChange={(next) => next && setTrendPeriod(next as TrendPeriod)}
                className="border border-border bg-secondary"
                aria-label="Trend period"
              >
                <ToggleGroupItem
                  value="year"
                  className="h-9 px-3 text-xs"
                  title="One point per year: its last quarter"
                >
                  Annual
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="quarter"
                  className="h-9 px-3 text-xs"
                  title="One point per quarter"
                >
                  Quarterly
                </ToggleGroupItem>
              </ToggleGroup>
            }
          >
            {trendQ.isError ? (
              <p className="text-sm text-destructive">Could not load the macro variable trend.</p>
            ) : trendQ.isPending ? (
              <Skeleton className="h-72 w-full" />
            ) : trendQ.data && !trendQ.data.hasData ? (
              <NoBandData label={bandLabel} />
            ) : trendQ.data === null || trend.series.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not Available Yet.</p>
            ) : trend.rows.every((row) =>
                trend.series.every((s) => (row as Record<string, unknown>)[s.dataKey] == null),
              ) ? (
              <p className="text-sm text-muted-foreground">
                {trendPeriod === "year"
                  ? "Not enough history for an annual comparison yet: it needs a quarter from a year earlier."
                  : "Not enough history for a quarterly comparison yet."}
              </p>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Macro variables shown">
                  {trend.series.map((s) => {
                    const on = !hiddenTrend.has(s.dataKey);
                    return (
                      <button
                        key={s.dataKey}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setHiddenTrend((prev) => {
                            const next = new Set(prev);
                            if (!next.delete(s.dataKey)) next.add(s.dataKey);
                            return next;
                          })
                        }
                        className={`flex items-center gap-1.5 border px-2 py-1 text-[11px] transition-colors ${
                          on
                            ? "border-border bg-secondary text-foreground"
                            : "border-border/50 text-muted-foreground opacity-50 hover:opacity-80"
                        }`}
                      >
                        <span
                          className="size-2 shrink-0"
                          style={{ backgroundColor: on ? s.color : "transparent", outline: `1px solid ${s.color}` }}
                          aria-hidden
                        />
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                {trend.series.every((s) => hiddenTrend.has(s.dataKey)) ? (
                  <p className="text-sm text-muted-foreground">Pick a variable above to plot it.</p>
                ) : (
                  <TrendLineChart
                    data={trend.rows as Array<{ month: string }>}
                    series={trend.series
                      .filter((s) => !hiddenTrend.has(s.dataKey))
                      .map((s) => ({ ...s, hideFromLegend: true }))}
                    suffix=""
                    decimals={2}
                    height={320}
                    valueLabel={trendLabel}
                    bands={trendBands}
                    yDomain={[0, 100]}
                  />
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Hover a point for the variable&apos;s sensitivity that {trendPeriod === "year" ? "year" : "quarter"}.{" "}
                  {tierBands(tiersQ.data?.tiers)
                    .map((b) => `${b.tier} ${b.from}${b.to >= 100 ? " and above" : `–${b.to}`}`)
                    .join(" · ")}
                </p>
              </>
            )}
          </Panel>

          <Sheet
            open={selected != null || selectedVariable != null}
            onOpenChange={(open) => {
              if (!open) {
                setSelected(null);
                setSelectedVariable(null);
              }
            }}
          >
            <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
              {selected ? (
                <>
                  <SheetHeader>
                    <SheetTitle className="text-base">{selected.name}</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                      {selected.tier} sensitivity
                      {" · "}
                      {selected.volume > 0
                        ? `${formatCompactCurrency(selected.volume)} volume`
                        : "volume Not Available Yet."}
                    </p>
                  </SheetHeader>

                  <div className="mt-6 border border-border bg-card">
                    <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Named factors driving this sensitivity
                    </p>
                    {selected.factors.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground">
                        No factor detail for this industry yet.
                      </p>
                    ) : null}
                    <ul className="divide-y divide-border">
                      {selected.factors.map((f) => (
                        <li key={f.factor} className="px-4 py-3">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm font-medium text-foreground">{macroFeatureLabel(f.factor)}</span>
                            <span
                              className="text-sm font-semibold"
                              style={{ color: TIER_COLOR[factorLabels.get(f.factor) ?? "Low"] }}
                            >
                              {factorLabels.get(f.factor)}
                            </span>
                          </div>
                          {/* Just the change against the last quarter, in the Athena amber. The factor's own
                              weight is the figure on the right. A factor with no prior quarter has no line. */}
                          {f.changePct !== null ? (
                            <p className="mt-1 text-xs leading-relaxed text-amber">
                              {f.changePct > 0 ? "+" : ""}
                              {f.changePct}% vs last quarter
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    <p className="border-t border-border px-4 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                      Each factor is labelled by its weight against this industry's strongest factor, on the same
                      High / Medium / Low bands as the sensitivity.
                    </p>
                  </div>
                </>
              ) : selectedVariable ? (
                <>
                  <SheetHeader>
                    <SheetTitle className="text-base">{selectedVariable.name}</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                      Sensitivity <TierText tier={tierForScore(selectedVariable.level ?? 0, tiersQ.data?.tiers)} />
                      {selectedVariable.previousLevel !== null ? (
                        <>
                          {" "}
                          (was <TierText tier={tierForScore(selectedVariable.previousLevel, tiersQ.data?.tiers)} />)
                        </>
                      ) : null}
                      {` · averaged over ${selectedVariable.segments} industries`}
                    </p>
                  </SheetHeader>

                  <div className="mt-6 border border-border bg-card">
                    <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Last 4 quarters
                    </p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="px-4 py-2 font-medium">Quarter</th>
                          <th className="px-4 py-2 text-right font-medium">Sensitivity</th>
                          <th className="px-4 py-2 text-right font-medium">Vs prior quarter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVariable.history
                          .map((q, i, all) => ({ q, prev: i > 0 ? all[i - 1]!.level : null }))
                          .reverse()
                          .map(({ q, prev }) => {
                            const tiers = tiersQ.data?.tiers;
                            const now = q.level !== null ? tierForScore(q.level, tiers) : null;
                            const before = prev !== null ? tierForScore(prev, tiers) : null;
                            const moved = now && before ? TIER_ORDER.indexOf(before) - TIER_ORDER.indexOf(now) : 0;
                            return (
                              <tr key={q.quarter} className="border-t border-border">
                                <td className="px-4 py-3 text-foreground">{q.label}</td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {now ? <TierText tier={now} /> : "—"}
                                </td>
                                <td className="px-4 py-3 text-right text-muted-foreground">
                                  {!now || !before ? "—" : moved > 0 ? "▲ Higher band" : moved < 0 ? "▼ Lower band" : "■ Same band"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </SheetContent>
          </Sheet>

          {/* The tier ranges, the Propose changes button, the approval queue and the change log are for the product admin only;
              everyone else still sees the tiers on the charts (Low / Medium / High), never the score ranges behind them. */}
          {isProductAdmin() ? <MacroTierConfigSection /> : null}
        </div>
      )}
    </div>
  );
}
